// VerificationPage.jsx - Email doğrulama kodu giriş sayfası
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';

const VerificationPage = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyCode, sendVerificationCode } = useAuth();
  
  // Email'i location state'den al
  const email = location.state?.email || '';

  useEffect(() => {
    if (!email) {
      navigate('/register');
    }
  }, [email, navigate]);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // İlk input'a focus
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index, value) => {
    // Sadece rakam kabul et
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Sonraki input'a geç
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Tüm kod girildiğinde otomatik doğrula
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Backspace ile önceki input'a geç
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newCode = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
      setCode(newCode);
      if (pastedData.length === 6) {
        handleVerify(pastedData);
      }
    }
  };

  const handleVerify = async (fullCode = code.join('')) => {
    if (fullCode.length !== 6) {
      setError('Lütfen 6 haneli kodu girin');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await verifyCode(email, fullCode);
      setSuccess(true);
      
      // 2 saniye sonra login sayfasına yönlendir
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Email adresiniz doğrulandı! Şimdi giriş yapabilirsiniz.' 
          } 
        });
      }, 2000);
    } catch (err) {
      console.error('Verification error:', err);
      const errorMessage = err.message || 'Doğrulama hatası oluştu';
      
      if (errorMessage.includes('Geçersiz')) {
        setError('Geçersiz doğrulama kodu. Lütfen tekrar deneyin.');
      } else if (errorMessage.includes('süresi')) {
        setError('Doğrulama kodunun süresi dolmuş. Yeni kod gönderin.');
      } else {
        setError(errorMessage);
      }
      
      // Kodu temizle
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    
    setResendLoading(true);
    setError('');

    try {
      await sendVerificationCode(email);
      setResendSuccess(true);
      setCountdown(60); // 60 saniye bekle
      
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err) {
      console.error('Resend error:', err);
      setError('Kod gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setResendLoading(false);
    }
  };

  if (!email) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 mb-4">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Email Doğrulama</h1>
          <p className="text-gray-400">
            <span className="text-blue-400">{email}</span> adresine gönderilen 6 haneli kodu girin
          </p>
        </div>

        {/* Card */}
        <div className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-8 border border-gray-700/50 shadow-xl">
          {success ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Email Doğrulandı!</h2>
              <p className="text-gray-400">Giriş sayfasına yönlendiriliyorsunuz...</p>
            </div>
          ) : (
            <>
              {/* Code Input */}
              <div className="flex justify-center gap-3 mb-6">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    disabled={loading}
                    className={`
                      w-12 h-14 text-center text-2xl font-bold rounded-lg
                      bg-gray-900/50 border-2 transition-all duration-200
                      focus:outline-none focus:ring-2 focus:ring-blue-500
                      ${error ? 'border-red-500' : digit ? 'border-blue-500' : 'border-gray-600'}
                      ${loading ? 'opacity-50 cursor-not-allowed' : ''}
                      text-white
                    `}
                  />
                ))}
              </div>

              {/* Error */}
              {error && (
                <div className="mb-4 p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-300 text-sm text-center">
                  {error}
                </div>
              )}

              {/* Resend Success */}
              {resendSuccess && (
                <div className="mb-4 p-3 bg-green-900/30 border border-green-500/50 rounded-lg text-green-300 text-sm text-center">
                  Yeni doğrulama kodu gönderildi!
                </div>
              )}

              {/* Verify Button */}
              <button
                onClick={() => handleVerify()}
                disabled={loading || code.join('').length !== 6}
                className={`
                  w-full py-3 px-4 rounded-lg font-semibold text-white
                  transition-all duration-200
                  ${loading || code.join('').length !== 6
                    ? 'bg-gray-600 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500'
                  }
                `}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Doğrulanıyor...
                  </span>
                ) : 'Doğrula'}
              </button>

              {/* Resend */}
              <div className="mt-6 text-center">
                <p className="text-gray-400 text-sm mb-2">Kod gelmedi mi?</p>
                <button
                  onClick={handleResend}
                  disabled={resendLoading || countdown > 0}
                  className={`
                    text-sm font-medium transition-colors
                    ${resendLoading || countdown > 0
                      ? 'text-gray-500 cursor-not-allowed'
                      : 'text-blue-400 hover:text-blue-300'
                    }
                  `}
                >
                  {resendLoading ? 'Gönderiliyor...' : countdown > 0 ? `Tekrar gönder (${countdown}s)` : 'Tekrar gönder'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Back to Register */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/register')}
            className="text-gray-400 hover:text-white text-sm transition-colors"
          >
            ← Kayıt sayfasına dön
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerificationPage;
